import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ChevronRight, Loader2, MessageCircle, PlayCircle } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useMoney } from '@/components/admin/expenses/moneyUi'
import { useReceiptScanEnabled } from '@/hooks/useReceiptScanEnabled'
import { getStoredUserRole } from '@/lib/staff-roles'
import { cn } from '@/lib/utils'
import { toastHelpers } from '@/lib/toast-helpers'
import {
  INBOX_TABS,
  RECEIPT_STATUS_LABEL,
  inboxCount,
  inboxTabOf,
  isReading,
  nextInQueue,
  reviewQueue,
  scanHeadline,
  type InboxTab,
} from '@/lib/receiptScan/receiptScanLogic'
import { ReceiptReviewSheet } from './ReceiptReviewSheet'
import { ReceiptScanButton } from './ReceiptScanButton'
import { PriceChangesCard } from '@/components/admin/expenses/PriceChangesCard'
import { formatLocalYMD } from '@/components/admin/expenses/expense-constants'
import { windowStart } from '@/lib/receiptScan/priceChanges'

const APPROVERS = new Set(['admin', 'manager', 'inventory_manager'])

function fmtDate(ymd: string): string {
  if (!ymd) return ''
  const d = new Date(`${ymd}T00:00:00`)
  return Number.isNaN(d.getTime()) ? ymd : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/**
 * Receipts inbox (F-FE-N192). Photograph a whole pile at once; the reader
 * works through it in the background; then review them one after another
 * (swipe on a phone, arrow keys on a computer). Everyone who scans sees their
 * own receipts here; approvers see everyone's.
 */
export function ReceiptsPage() {
  const role = getStoredUserRole()
  const money = useMoney()
  const { enabled } = useReceiptScanEnabled()
  const [tab, setTab] = useState<InboxTab>('review')
  const [openId, setOpenId] = useState<string | null>(null)
  const [queue, setQueue] = useState<string[] | undefined>(undefined)

  const listQ = useQuery({
    queryKey: ['receiptScans'],
    queryFn: () => apiClient.listReceiptScans(),
    enabled,
    // Keep polling while anything is still being read, so the pile fills in.
    refetchInterval: q => ((q.state.data?.data?.scans ?? []).some(s => isReading(s.status)) ? 3000 : 30_000),
  })
  const scans = listQ.data?.data?.scans ?? []
  const counts = listQ.data?.data?.counts ?? {}
  const configured = listQ.data?.data?.configured ?? true
  const shown = useMemo(() => scans.filter(s => inboxTabOf(s.status) === tab), [scans, tab])
  const pending = useMemo(() => reviewQueue(scans), [scans])
  const priceTo = formatLocalYMD(new Date())
  const priceFrom = windowStart(priceTo, 30)

  const startReview = (ids = pending) => {
    if (!ids.length) return
    setQueue(ids)
    setOpenId(ids[0])
  }
  const onDone = (doneId: string) => {
    if (!queue) return
    const open = new Set(reviewQueue(scans).filter(id => id !== doneId))
    const next = nextInQueue(queue, doneId, open)
    if (next) setOpenId(next)
    else {
      setOpenId(null)
      setQueue(undefined)
      toastHelpers.success('All caught up', 'Every receipt in this pile is reviewed.')
    }
  }

  if (!enabled) {
    return (
      <div className="p-4 sm:p-6">
        <h1 className="text-3xl font-bold tracking-tight">Receipts</h1>
        <p className="mt-2 text-muted-foreground">Receipt scanning is not switched on for this restaurant.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight">Receipts</h1>
          <p className="mt-1 text-muted-foreground">
            Photograph receipts as you buy — or the whole pile at once — and they're read for you.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:shrink-0 sm:pt-1">
          {APPROVERS.has(role) && pending.length > 0 && (
            <Button type="button" variant="outline" onClick={() => startReview()}>
              <PlayCircle className="mr-1 h-4 w-4" />
              Review {pending.length}
            </Button>
          )}
          <ReceiptScanButton
            variant="default"
            label="Scan receipts"
            onScanned={id => {
              setQueue(undefined)
              setOpenId(id)
            }}
            onBatch={() => setTab('reading')}
          />
        </div>
      </div>

      {!configured && (
        <div className="flex gap-2 rounded-lg border border-amber-500/50 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Receipt reading isn't set up for this restaurant yet, so new photos can't be read.
        </div>
      )}

      <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1 sm:w-fit">
        {INBOX_TABS.map(t => {
          const n = inboxCount(counts, t.key)
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                tab === t.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
              {n > 0 && <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">{n}</span>}
            </button>
          )
        })}
      </div>

      {listQ.isLoading ? (
        <Skeleton className="h-64" />
      ) : shown.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          {tab === 'review'
            ? 'Nothing to review. Scan a receipt to get started.'
            : tab === 'reading'
              ? 'Nothing is being read right now.'
              : tab === 'failed'
                ? 'Every receipt was read.'
                : 'Approved and discarded receipts show here.'}
        </div>
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {shown.map(s => {
            const h = scanHeadline(s)
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (tab === 'review' && APPROVERS.has(role)) startReview([s.id, ...pending.filter(id => id !== s.id)])
                    else {
                      setQueue(undefined)
                      setOpenId(s.id)
                    }
                  }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{h.vendor}</span>
                      {s.source === 'whatsapp' && <MessageCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="Sent on WhatsApp" />}
                    </div>
                    <div className="truncate text-muted-foreground">
                      {[h.date && fmtDate(h.date), s.created_by_name && `scanned by ${s.created_by_name}`, s.status === 'failed' ? s.error : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  {h.total > 0 && <span className="font-medium tabular-nums">{money.full(h.total)}</span>}
                  <span className="hidden w-32 text-right text-muted-foreground sm:block">
                    {isReading(s.status) ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading…
                      </span>
                    ) : (
                      RECEIPT_STATUS_LABEL[s.status]
                    )}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {/* F-RPT-N46: what stock costs now against before — the people who
          approve receipts are the ones who notice a supplier creeping up. */}
      {(role === 'admin' || role === 'manager') && (
        <PriceChangesCard from={priceFrom} to={priceTo} periodLabel="Last 30 days" />
      )}

      <ReceiptReviewSheet
        scanId={openId}
        queue={queue}
        onNavigate={setOpenId}
        onDone={onDone}
        onClose={() => {
          setOpenId(null)
          setQueue(undefined)
        }}
      />
    </div>
  )
}
